import React, { useState } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { FeatherCheck, FeatherFileText, FeatherUndo2 } from "@subframe/core";
import {
  AgentReviewFinding,
  FindingDisposition,
} from "../../domain/entities/AgentReviewEntities";

/**
 * ZOUR-67 — one finding.
 *
 * The layout is the finding's own contract made visible: what the document
 * says, what we are about to post, why they disagree, and the quoted evidence
 * underneath. Nothing here proposes a value, because nothing upstream is
 * allowed to — a guardrail rejects a finding whose reason says what to type.
 *
 * "Not an error" is the only control. It is a judgement about the *finding*,
 * not about the bill, and it never edits the bill: correcting the bill is the
 * review page's job and this page links there rather than duplicating it.
 */

const SEVERITY_VARIANT: Record<string, "error" | "warning" | "neutral"> = {
  high: "error",
  medium: "warning",
  low: "neutral",
};

interface Props {
  finding: AgentReviewFinding;
  canEdit: boolean;
  onDisposition: (id: number, value: FindingDisposition) => Promise<void>;
}

export const FindingCard: React.FC<Props> = ({ finding, canEdit, onDisposition }) => {
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const dismissed = finding.disposition === "not_an_error";
  const blocking = finding.finding_group === "document_mismatch";

  // Some findings are about a value being ambiguous rather than wrong — the same
  // bill number on two invoices in one bundle, say. Those arrive with both sides
  // identical, and the two-column "document says / bill holds" layout then reads
  // as a contradiction of itself: the reader trips on "211 vs 211" and never gets
  // to the sentence that explains it. When the two agree, lead with the reason.
  const sameValue =
    (finding.pdf_value ?? "").trim().toLowerCase() ===
      (finding.our_value ?? "").trim().toLowerCase() &&
    (finding.pdf_value ?? "").trim() !== "";

  const apply = async (value: FindingDisposition) => {
    setSaving(true);
    setFailed(null);
    try {
      await onDisposition(finding.id, value);
    } catch (err) {
      setFailed(err instanceof Error ? err.message : "Could not save that");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`flex w-full flex-col gap-3 rounded-md border border-solid p-4 ${
        dismissed
          ? "border-neutral-border bg-neutral-50 opacity-60"
          : blocking
            ? "border-error-200 bg-error-50"
            : "border-neutral-border bg-default-background"
      }`}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={blocking ? "error" : "neutral"}>
            {blocking ? "Document mismatch" : "Booking advisory"}
          </Badge>
          <Badge variant={SEVERITY_VARIANT[finding.severity] ?? "neutral"}>
            {finding.severity}
          </Badge>
          {finding.page !== null && (
            <span className="text-caption font-caption text-subtext-color">
              page {finding.page}
            </span>
          )}
        </div>
        {dismissed && <Badge variant="neutral">Not an error</Badge>}
      </div>

      <span className="text-body-bold font-body-bold text-default-font break-words">
        {finding.field}
      </span>

      {sameValue ? (
        <>
          {finding.reason && (
            <span className="text-body font-body text-default-font">
              {finding.reason}
            </span>
          )}
          <span className="text-caption font-caption text-subtext-color break-words">
            Both read: {finding.pdf_value?.trim()}
          </span>
        </>
      ) : (
        <>
          <div className="flex w-full flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 flex-col gap-1 rounded-sm bg-neutral-50 p-2">
              <span className="text-caption font-caption text-subtext-color">
                The document says
              </span>
              <span className="text-body font-body text-default-font break-words">
                {finding.pdf_value?.trim() || "—"}
              </span>
            </div>
            <div className="flex flex-1 flex-col gap-1 rounded-sm bg-neutral-50 p-2">
              <span className="text-caption font-caption text-subtext-color">
                This bill holds
              </span>
              <span className="text-body font-body text-default-font break-words">
                {finding.our_value?.trim() || "—"}
              </span>
            </div>
          </div>

          {finding.reason && (
            <span className="text-body font-body text-default-font">
              {finding.reason}
            </span>
          )}
        </>
      )}

      {finding.evidence && (
        <div className="flex w-full items-start gap-2 rounded-sm border-l-2 border-solid border-neutral-300 bg-neutral-50 p-2">
          <FeatherFileText className="mt-0.5 shrink-0 text-body font-body text-subtext-color" />
          <span className="text-caption font-caption italic text-subtext-color break-words">
            {finding.evidence}
          </span>
        </div>
      )}

      {failed && (
        <span className="text-caption font-caption text-error-700">{failed}</span>
      )}

      {canEdit && (
        <div className="flex items-center gap-2">
          {dismissed ? (
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherUndo2 />}
              loading={saving}
              onClick={() => apply(null)}
            >
              Undo
            </Button>
          ) : (
            <Button
              variant="neutral-secondary"
              size="small"
              icon={<FeatherCheck />}
              loading={saving}
              onClick={() => apply("not_an_error")}
            >
              Not an error
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default FindingCard;
